import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  getParceirosAdminControllerListarQueryKey,
  useParceirosAdminControllerAtualizar,
  useParceirosAdminControllerCriar,
} from '@/api/generated/admin-parceiros/admin-parceiros';
import type { CriarParceiroDtoCategoria, ParceiroAdminResponse } from '@/api/generated/model';
import { CriarParceiroDtoCategoria as CATEGORIAS } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

const SEM_CATEGORIA = 'sem-categoria';

/**
 * Cadastro e edição do perfil comercial.
 *
 * ⚠️ As imagens NÃO ficam aqui: logo e capa são rotas próprias e só existem
 * depois que o parceiro existe (o arquivo é gravado sob o tenant dele). Na
 * criação, o formulário salva primeiro e a tela abre o envio em seguida.
 */
export function ParceiroFormDialogo({
  aberto,
  aoFechar,
  parceiro,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parceiro?: ParceiroAdminResponse;
}) {
  const queryClient = useQueryClient();
  const criar = useParceirosAdminControllerCriar();
  const atualizar = useParceirosAdminControllerAtualizar();

  const [nome, setNome] = useState(parceiro?.nome ?? '');
  const [categoria, setCategoria] = useState<string>(parceiro?.categoria ?? SEM_CATEGORIA);
  const [descricao, setDescricao] = useState(parceiro?.descricao ?? '');
  const [link, setLink] = useState(parceiro?.link ?? '');
  const [naSidebar, setNaSidebar] = useState(parceiro?.mostrarNaSidebar ?? false);
  const [erros, setErros] = useState<Record<string, string[]>>({});

  const salvando = criar.isPending || atualizar.isPending;

  function salvar(): void {
    setErros({});

    const dados = {
      nome: nome.trim(),
      categoria: (categoria === SEM_CATEGORIA ? null : categoria) as CriarParceiroDtoCategoria,
      descricao: descricao.trim() || null,
      link: link.trim() || null,
      mostrarNaSidebar: naSidebar,
    };

    const aoDarCerto = () => {
      void queryClient.invalidateQueries({
        queryKey: getParceirosAdminControllerListarQueryKey(),
      });
      toast.success(parceiro ? 'Parceiro atualizado' : 'Parceiro cadastrado');
      aoFechar();
    };

    const aoFalhar = (erro: unknown) => {
      if (erro instanceof ApiError && erro.detalhes) {
        setErros(erro.detalhes);
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível salvar');
    };

    if (parceiro) {
      atualizar.mutate({ id: parceiro.id, data: dados }, { onSuccess: aoDarCerto, onError: aoFalhar });
    } else {
      criar.mutate({ data: dados }, { onSuccess: aoDarCerto, onError: aoFalhar });
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{parceiro ? 'Editar parceiro' : 'Novo parceiro'}</DialogTitle>
          <DialogDescription>
            {parceiro
              ? 'O identificador (slug) não muda — ele já pode estar em links e relatórios.'
              : 'O ambiente do parceiro é criado junto. Logo e capa são enviados depois.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Campo rotulo="Nome" erro={erros.nome}>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200} />
          </Campo>

          <Campo rotulo="Categoria" erro={erros.categoria}>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SEM_CATEGORIA}>Sem categoria</SelectItem>
                {Object.values(CATEGORIAS).map((opcao) => (
                  <SelectItem key={opcao} value={opcao}>
                    {opcao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo rotulo="Descrição curta" erro={erros.descricao}>
            <Textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {descricao.length}/200 — 2 a 3 linhas, como pede o guia do parceiro.
            </p>
          </Campo>

          <Campo rotulo="Link do site" erro={erros.link}>
            <Input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://site-do-parceiro.com.br"
            />
          </Campo>

          <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-border bg-muted/30">
            <div>
              <Label className="text-sm font-medium">Mostrar no carrossel da sidebar</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Rodízio a cada 15 segundos, dois por vez e de categorias diferentes.
              </p>
            </div>
            <Switch checked={naSidebar} onCheckedChange={setNaSidebar} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={salvando}
            className="bg-brand text-brand-foreground hover:bg-brand/90"
          >
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
            {parceiro ? 'Salvar' : 'Cadastrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{rotulo}</Label>
      {children}
      {erro && erro.length > 0 && <p className="text-xs text-destructive">{erro[0]}</p>}
    </div>
  );
}
